import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const response = await axios.post(`${API_URL}/api/auth/refresh`, null, {
            params: { refresh_token: refreshToken }
          });
          
          const { access_token, refresh_token: newRefreshToken } = response.data;
          localStorage.setItem('access_token', access_token);
          localStorage.setItem('refresh_token', newRefreshToken);
          
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        window.location.href = '/';
      }
    }
    
    return Promise.reject(error);
  }
);

export const auth = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  googleAuth: (token) => api.post('/auth/google', { access_token: token }),
  me: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/me', data),
  requestCode: (email) => api.post('/auth/request-code', { email }),
  verifyCode: (email, code) => api.post('/auth/verify-code', { email, code }),
  resetPassword: (email, code, new_password) => api.post('/auth/reset-password', { email, code, new_password }),
};

export const tournaments = {
  list: (params) => api.get('/tournaments', { params }),
  get: (id) => api.get(`/tournaments/${id}`),
  create: (data) => api.post('/tournaments', data),
  update: (id, data) => api.put(`/tournaments/${id}`, data),
  delete: (id) => api.delete(`/tournaments/${id}`),
  register: (id) => api.post(`/tournaments/${id}/register`),
  unregister: (id) => api.delete(`/tournaments/${id}/register`),
  expressInterest: (id) => api.post(`/tournaments/${id}/interest`),
  removeInterest: (id) => api.delete(`/tournaments/${id}/interest`),
  joinWaitlist: (id) => api.post(`/tournaments/${id}/waitlist/join`),
  leaveWaitlist: (id) => api.delete(`/tournaments/${id}/waitlist/leave`),
  confirmWaitlist: (id) => api.post(`/tournaments/${id}/waitlist/confirm`),
  declineWaitlist: (id) => api.post(`/tournaments/${id}/waitlist/decline`),
  waitlistStatus: (id) => api.get(`/tournaments/${id}/waitlist/me`),
  checkoutTournament: (data) => api.post('/stripe/checkout/tournament', data),
  checkoutStatus: (paymentIntentId) => api.get(`/stripe/checkout/status/${paymentIntentId}`),
};

export const availabilities = {
  list: (params) => api.get('/player-events', { params }),
  get: (id) => api.get(`/player-events/${id}`),
  create: (data) => api.post('/player-events', data),
  update: (id, data) => api.put(`/player-events/${id}`, data),
  delete: (id) => api.delete(`/player-events/${id}`),
};

export const playerEvents = {
  list: (params) => api.get('/player-events', { params }),
  get: (id) => api.get(`/player-events/${id}`),
  create: (data) => api.post('/player-events', data),
  update: (id, data) => api.put(`/player-events/${id}`, data),
  remove: (id) => api.delete(`/player-events/${id}`),
  interest: (id) => api.post(`/player-events/${id}/interest`),
  removeInterest: (id) => api.delete(`/player-events/${id}/interest`),
  confirm: (id, pid) => api.post(`/player-events/${id}/confirm/${pid}`),
  refuse: (id, pid) => api.post(`/player-events/${id}/refuse/${pid}`),
  start: (id) => api.post(`/player-events/${id}/start`),
  finish: (id) => api.post(`/player-events/${id}/finish`),
  ranking: (id) => api.get(`/player-events/${id}/ranking`),
  getMessages: (id, after) => api.get(`/player-events/${id}/messages`, { params: after ? { after } : {} }),
  postMessage: (id, text) => api.post(`/player-events/${id}/messages`, { text }),
  getMatches: (id) => api.get(`/player-events/${id}/matches`),
  createMatch: (id, data) => api.post(`/player-events/${id}/matches`, data),
  updateMatch: (id, mid, data) => api.put(`/player-events/${id}/matches/${mid}`, data),
  deleteMatch: (id, mid) => api.delete(`/player-events/${id}/matches/${mid}`),
};

export const notifications = {
  list: () => api.get('/notifications'),
  unreadCount: () => api.get('/notifications/unread-count'),
  markAllRead: () => api.post('/notifications/mark-all-read'),
  markRead: (id) => api.post(`/notifications/${id}/read`),
};

export const admin = {
  getStats: () => api.get('/admin/stats'),
  // Users
  getUsers: (params) => api.get('/admin/users', { params }),
  getUser: (userId) => api.get(`/admin/users/${userId}`),
  getUserDetails: (userId) => api.get(`/admin/users/${userId}/details`),
  updateUser: (userId, data) => api.put(`/admin/users/${userId}`, data),
  updateUserRole: (userId, role) => api.put(`/admin/users/${userId}/role?role=${role}`),
  toggleArcanien: (userId, data) => api.put(`/admin/users/${userId}/arcanien`, data),
  resetPassword: (userId) => api.post(`/admin/users/${userId}/reset-password`),
  banUser: (userId, reason) => api.post(`/admin/users/${userId}/ban`, { reason }),
  unbanUser: (userId) => api.post(`/admin/users/${userId}/unban`),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  // Tournaments
  getTournaments: (params) => api.get('/admin/tournaments', { params }),
  updateTournament: (id, data) => api.put(`/admin/tournaments/${id}`, data),
  deleteTournament: (id) => api.delete(`/admin/tournaments/${id}`),
  // Availabilities
  getAvailabilities: (params) => api.get('/admin/availabilities', { params }),
  updateAvailability: (id, data) => api.put(`/admin/availabilities/${id}`, data),
  deleteAvailability: (id) => api.delete(`/admin/availabilities/${id}`),
  // Reports
  getReports: (params) => api.get('/admin/reports', { params }),
  updateReport: (id, data) => api.put(`/admin/reports/${id}`, data),
  deleteReport: (id) => api.delete(`/admin/reports/${id}`),
  // Popups
  getPopups: () => api.get('/admin/popups'),
  createPopup: (data) => api.post('/admin/popups', data),
  updatePopup: (id, data) => api.put(`/admin/popups/${id}`, data),
  deletePopup: (id) => api.delete(`/admin/popups/${id}`),
  // Activity Logs
  getActivityLogs: (params) => api.get('/admin/activity-logs', { params }),
};

export const users = {
  getMyTournaments: () => api.get('/users/me/tournaments'),
  search: (q) => api.get(`/users/search?q=${encodeURIComponent(q)}`),
  getProfile: (userId) => api.get(`/users/${userId}`),
};

export const gameFormats = {
  list: () => api.get('/game-formats'),
};

export const org = {
  getMyTournaments: (params) => api.get('/org/my-tournaments', { params }),
  getStats: () => api.get('/org/stats'),
  getIframeConfig: () => api.get('/org/iframe-config'),
  updateIframeConfig: (data) => api.put('/org/iframe-config', data),
  uploadGameLogo: (formData) => api.post('/org/game-logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getRoles: () => api.get('/org/roles'),
  createRole: (data) => api.post('/org/roles', data),
  updateRole: (id, data) => api.put(`/org/roles/${id}`, data),
  deleteRole: (id) => api.delete(`/org/roles/${id}`),
  getMembers: () => api.get('/org/members'),
  addMember: (data) => api.post('/org/members', data),
  updateMember: (id, data) => api.put(`/org/members/${id}`, data),
  removeMember: (id) => api.delete(`/org/members/${id}`),
};

export const agenda = {
  getEvents: (params) => api.get('/agenda', { params }),
};

export const messaging = {
  getUnreadCount: () => api.get('/messaging/unread-count'),
};

export const teammateRequests = {
  create: (data) => api.post('/teammate-requests', data),
  getForEvent: (eventType, eventId) => api.get(`/teammate-requests/event/${eventType}/${eventId}`),
  getMy: () => api.get('/teammate-requests/my'),
  update: (id, data) => api.put(`/teammate-requests/${id}`, data),
  remove: (id) => api.delete(`/teammate-requests/${id}`),
};

export const conversations = {
  list: () => api.get('/conversations'),
  open: (participant_id) => api.post('/conversations', { participant_id }),
  messages: (id, after) => api.get(`/conversations/${id}/messages`, { params: after ? { after } : {} }),
  send: (id, text) => api.post(`/conversations/${id}/messages`, { text }),
  contacts: () => api.get('/conversations/contacts/list'),
  unreadCount: () => api.get('/messaging/unread-count'),
};

export const tournamentPush = {
  quota: (tid) => api.get(`/tournaments/${tid}/push/quota`),
  send: (tid) => api.post(`/tournaments/${tid}/push/send`, { origin_url: window.location.origin }),
  paymentStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
  adminSet: (shopId, data) => api.put(`/admin/shops/${shopId}/push-settings`, data),
  history: (shopId) => api.get(`/admin/shops/${shopId}/push-history`),
};

export const tournamentAdmin = {
  getParticipants: (id) => api.get(`/tournaments/${id}/participants-details`),
  addPlayer: (id, search) => api.post(`/tournaments/${id}/add-player`, { search }),
  addPlayerManual: (id, data) => api.post(`/tournaments/${id}/add-player`, data),
  updatePayment: (id, playerId, payment_method) => api.put(`/tournaments/${id}/participants/${playerId}/payment`, { payment_method }),
  removePlayer: (id, playerId) => api.delete(`/tournaments/${id}/remove-player/${playerId}`),
  downloadCsv: (id) => api.get(`/tournaments/${id}/csv`, { responseType: 'blob' }),
  downloadXlsx: (id) => api.get(`/tournaments/${id}/xlsx`, { responseType: 'blob' }),
  closeTournament: (id) => api.post(`/tournaments/${id}/close`),
  getWaitlist: (id) => api.get(`/tournaments/${id}/waitlist`),
  addToWaitlist: (id, data) => api.post(`/tournaments/${id}/waitlist/add-manual`, data),
  relanceWaitlist: (id) => api.post(`/tournaments/${id}/waitlist/relance`),
};

export const rounds = {
  getState: (id) => api.get(`/tournaments/${id}/state`),
  start: (id, config) => api.post(`/tournaments/${id}/start`, config),
  nextRound: (id) => api.post(`/tournaments/${id}/rounds/next`),
  getRounds: (id) => api.get(`/tournaments/${id}/rounds`),
  getRound: (id, num) => api.get(`/tournaments/${id}/rounds/${num}`),
  submitResult: (id, num, data) => api.put(`/tournaments/${id}/rounds/${num}/result`, data),
  getStandings: (id) => api.get(`/tournaments/${id}/standings`),
  dropPlayer: (id, playerId) => api.post(`/tournaments/${id}/drop/${playerId}`),
  startTopCut: (id, size) => api.post(`/tournaments/${id}/top-cut`, { size }),
  getPrizes: (id) => api.get(`/tournaments/${id}/prizes`),
  addPrize: (id, data) => api.post(`/tournaments/${id}/prizes`, data),
  removePrize: (id, prizeId) => api.delete(`/tournaments/${id}/prizes/${prizeId}`),
  raffle: (id) => api.post(`/tournaments/${id}/raffle`),
  complete: (id) => api.post(`/tournaments/${id}/complete`),
  getParticipantsInfo: (id) => api.get(`/tournaments/${id}/participants-info`),
};

export const publicApi = {
  listTournaments: (params) => axios.get(`${API_URL}/api/public/tournaments`, { params }),
  getTournament: (id) => axios.get(`${API_URL}/api/public/tournaments/${id}`),
  listAvailabilities: (params) => axios.get(`${API_URL}/api/public/availabilities`, { params }),
  getAvailability: (id) => axios.get(`${API_URL}/api/public/availabilities/${id}`),
};

export const arcanien = {
  getInfo: () => axios.get(`${API_URL}/api/arcanien/info`),
  activatePromo: (code) => api.post('/arcanien/activate-promo', { code }),
  becomePro: () => api.post('/arcanien/become-pro'),
  gift: (data) => api.post('/arcanien/gift', data),
  getGifts: () => api.get('/arcanien/gifts'),
};

export const promoCodes = {
  list: () => api.get('/admin/promo-codes'),
  createBatch: (data) => api.post('/admin/promo-codes/batch', data),
  delete: (id) => api.delete(`/admin/promo-codes/${id}`),
};

export const feedPreferences = {
  get: () => api.get('/auth/feed-preferences'),
  update: (data) => api.put('/auth/feed-preferences', data),
};

export const social = {
  getFeed: (params) => api.get('/social/feed', { params }),
  getPublicFeed: (params) => api.get('/social/posts/user/public', { params }),
  createPost: (data) => api.post('/social/posts', data),
  getUserPosts: (userId, params) => api.get(`/social/posts/user/${userId}`, { params }),
  deletePost: (postId) => api.delete(`/social/posts/${postId}`),
  toggleLike: (postId) => api.post(`/social/posts/${postId}/like`),
  getComments: (postId, params) => api.get(`/social/posts/${postId}/comments`, { params }),
  addComment: (postId, data) => api.post(`/social/posts/${postId}/comments`, data),
  toggleCommentLike: (commentId) => api.post(`/social/comments/${commentId}/like`),
  votePoll: (postId, idx) => api.post(`/social/posts/${postId}/vote/${idx}`),
  followUser: (userId) => api.post(`/social/users/${userId}/follow`),
  unfollowUser: (userId) => api.delete(`/social/users/${userId}/follow`),
  getFollowStatus: (userId, viewerId) => api.get(`/social/users/${userId}/follow-status`, { params: { viewer_id: viewerId } }),
  getSocialStats: (userId) => api.get(`/social/users/${userId}/stats`),
};

export const userLimits = {
  get: () => api.get('/users/me/limits'),
};

export const tournamentInterest = {
  express: (id) => api.post(`/tournaments/${id}/interest`),
  remove: (id) => api.delete(`/tournaments/${id}/interest`),
};

export const teams = {
  list: (tournamentId) => api.get(`/tournaments/${tournamentId}/teams`),
  create: (tournamentId, data) => api.post(`/tournaments/${tournamentId}/teams`, data),
  update: (tournamentId, teamId, data) => api.put(`/tournaments/${tournamentId}/teams/${teamId}`, data),
  delete: (tournamentId, teamId) => api.delete(`/tournaments/${tournamentId}/teams/${teamId}`),
  leave: (tournamentId, teamId) => api.post(`/tournaments/${tournamentId}/teams/${teamId}/leave`),
  pay: (tournamentId, teamId) => api.post(`/tournaments/${tournamentId}/teams/${teamId}/pay`),
};

export default api;
